import React, { useState, useEffect, useRef } from 'react';
import { tasksApi } from '../../lib/tasks-api';
import { toast } from '../../lib/toast';
import { LdSheet } from '../ui/LdSheet';
import { MIcon } from '../../lib/micon';
import { QuickMicButton } from '../diktat/QuickMicButton';
import { avatarColor } from '../../lib/utils';
import { API_BASE, apiHeaders, leseFallback } from '../../lib/api';
import { LdAuswahl, LdDatum } from '../ld/LdFenster';

interface Props {
  open: boolean;
  onClose: () => void;
  onErstellt: () => void;
  initialTitel?: string;
  initialBeschreibung?: string;
  quelle?: 'manuell' | 'radar' | 'mail';
  quelle_ref_id?: string;
  quelle_ref_typ?: 'radar' | 'mail_message';
  // Welle U6b: Verknüpfung vorbelegen (Radar-Insight → Schüler/Klasse), damit "Zur Quelle" auflösbar ist
  initialSchuelerId?: number | null;
  initialKlasseId?: number | null;
}

interface SchuelerOpt { id: number; vorname: string; nachname: string; }
interface KlasseOpt { klasse_id: number; klasse_name: string; }

export function TaskErstellenModal({
  open, onClose, onErstellt,
  initialTitel = '', initialBeschreibung = '',
  quelle = 'manuell', quelle_ref_id, quelle_ref_typ, initialSchuelerId = null, initialKlasseId = null,
}: Props) {
  const [titel,       setTitel]       = useState(initialTitel);
  const [beschr,      setBeschr]      = useState(initialBeschreibung);
  const [faelligAm,   setFaelligAm]   = useState('');
  const [prioritaet,  setPrioritaet]  = useState<'normal' | 'hoch'>('normal');
  const [schuelerId,  setSchuelerId]  = useState<number | ''>('');
  const [klasseId,    setKlasseId]    = useState<number | ''>('');
  const [schuelerListe, setSchuelerListe] = useState<SchuelerOpt[]>([]);
  const [klassenListe,  setKlassenListe]  = useState<KlasseOpt[]>([]);
  const [laden,       setLaden]       = useState(false);
  const titelRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTitel(initialTitel);
      setBeschr(initialBeschreibung);
      setFaelligAm('');
      setPrioritaet('normal');
      setSchuelerId(initialSchuelerId ?? '');
      setKlasseId(initialKlasseId ?? '');
      setTimeout(() => titelRef.current?.focus(), 60);
      // Mandantengescopte Listen für die Verknüpfung
      fetch(`${API_BASE}/api/schueler`, { headers: apiHeaders() }).then(leseFallback<any[]>([], '/api/schueler')).then(setSchuelerListe).catch(() => {});
      fetch(`${API_BASE}/api/meine-klassen`, { headers: apiHeaders() }).then(leseFallback<any[]>([], '/api/meine-klassen'))
        .then((rows: any[]) => {
          const seen = new Map<number, KlasseOpt>();
          for (const r of rows) if (!seen.has(r.klasse_id)) seen.set(r.klasse_id, { klasse_id: r.klasse_id, klasse_name: r.klasse_name });
          setKlassenListe(Array.from(seen.values()));
        }).catch(() => {});
    }
  }, [open, initialTitel, initialBeschreibung, initialSchuelerId, initialKlasseId]);

  if (!open) return null;

  async function handleErstellen() {
    if (!titel.trim()) return;
    setLaden(true);
    try {
      await tasksApi.erstellen({
        titel: titel.trim(),
        beschreibung: beschr.trim() || undefined,
        faellig_am: faelligAm || undefined,
        quelle,
        quelle_ref_id,
        quelle_ref_typ,
        prioritaet,
        schueler_id: schuelerId === '' ? null : schuelerId,
        klasse_id: klasseId === '' ? null : klasseId,
      });
      onErstellt();
      onClose();
    } catch (err: any) {
      toast(err.message ?? 'Fehler beim Erstellen.', 'error');
    } finally {
      setLaden(false);
    }
  }

  const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: 'var(--ld-text-primary)', display: 'block', marginBottom: 6 };
  const inp: React.CSSProperties = {
    width: '100%', padding: '10px 13px', borderRadius: 12,
    border: '1.5px solid var(--ld-field-border)', fontSize: 15, outline: 'none',
    boxSizing: 'border-box', background: 'var(--ld-bg-card)', color: 'var(--ld-text-primary)',
  };

  const gewaehlterSchueler = schuelerId !== '' ? schuelerListe.find(s => s.id === schuelerId) : undefined;
  const gewaehlteKlasse    = klasseId !== '' ? klassenListe.find(k => k.klasse_id === klasseId) : undefined;

  return (
    <LdSheet open onClose={onClose} maxWidth={520} ariaLabel="Neue Aufgabe"
      title={quelle === 'radar' ? 'Aufgabe von Radar' : quelle === 'mail' ? 'Aufgabe von Mail' : 'Neue Aufgabe'}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Aufgabe + Mic */}
        <div>
          <label style={labelStyle}>Aufgabe</label>
          <div className="ld-compose-recipient" style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1.5px solid var(--ld-field-border)', borderRadius: 12, padding: '3px 5px 3px 12px', background: 'var(--ld-bg-card)' }}>
            <input ref={titelRef} className="ld-compose-bare" value={titel} onChange={e => setTitel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleErstellen()}
              placeholder="Was muss erledigt werden?"
              style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', padding: '8px 0', fontSize: 15, outline: 'none', color: 'var(--ld-text-primary)' }} />
            <QuickMicButton modul="task" size="sm" />
          </div>
        </div>

        {/* Beschreibung */}
        <div>
          <label style={labelStyle}>Beschreibung (optional)</label>
          <textarea value={beschr} onChange={e => setBeschr(e.target.value)}
            rows={2} placeholder="Details …" style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        {/* Fällig am + Priorität */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 150px', minWidth: 0 }}>
            <label style={labelStyle}>Fällig am (optional)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <LdDatum value={faelligAm} onChange={e => setFaelligAm(e.target.value)} />
              {faelligAm && <button onClick={() => setFaelligAm('')} aria-label="Datum entfernen" style={{ padding: '0 12px', borderRadius: 12, border: '1.5px solid var(--ld-field-border)', background: 'var(--ld-bg-input)', fontSize: 13, cursor: 'pointer', color: 'var(--ld-text-secondary)' }}>×</button>}
            </div>
          </div>
          <div style={{ flex: '0 0 auto' }}>
            <label style={labelStyle}>Priorität</label>
            <div style={{ display: 'inline-flex', gap: 4, background: '#EEEBF6', borderRadius: 12, padding: 4 }}>
              {(['normal', 'hoch'] as const).map(p => {
                const aktiv = prioritaet === p;
                return (
                  <button key={p} onClick={() => setPrioritaet(p)} aria-pressed={aktiv} style={{
                    border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    background: aktiv ? (p === 'hoch' ? '#DC2626' : 'linear-gradient(135deg,#6B5FD4,#4A3FB0)') : 'transparent',
                    color: aktiv ? '#fff' : 'var(--ld-text-secondary)',
                  }}>{p === 'hoch' ? 'Hoch' : 'Normal'}</button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Verknüpfen */}
        <div>
          <label style={labelStyle}>Verknüpfen (optional)</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Schüler */}
            {gewaehlterSchueler ? (
              <span className="ld-task-chip" style={{ alignSelf: 'flex-start', padding: '4px 6px 4px 4px', fontSize: 13 }}>
                <span className="ld-task-chip__av" style={{ width: 22, height: 22, fontSize: 10, background: avatarColor(gewaehlterSchueler.id).bg, color: avatarColor(gewaehlterSchueler.id).fg }}>
                  {(gewaehlterSchueler.vorname[0] + gewaehlterSchueler.nachname[0]).toUpperCase()}
                </span>
                {gewaehlterSchueler.vorname} {gewaehlterSchueler.nachname}
                <button onClick={() => setSchuelerId('')} aria-label="Schüler entfernen" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ld-text-secondary)', display: 'flex', padding: 2 }}><MIcon name="close" size={15} /></button>
              </span>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MIcon name="person_add" size={18} color="var(--ld-text-tertiary)" />
                <LdAuswahl value="" onChange={e => setSchuelerId(e.target.value ? Number(e.target.value) : '')} aria-label="Schüler verknüpfen">
                  <option value="">Schüler verknüpfen …</option>
                  {schuelerListe.map(s => <option key={s.id} value={s.id}>{s.nachname}, {s.vorname}</option>)}
                </LdAuswahl>
              </div>
            )}
            {/* Klasse */}
            {gewaehlteKlasse ? (
              <span className="ld-task-chip" style={{ alignSelf: 'flex-start', padding: '6px 8px 6px 10px', fontSize: 13 }}>
                <MIcon name="groups" size={15} color="var(--ld-text-tertiary)" />
                {gewaehlteKlasse.klasse_name}
                <button onClick={() => setKlasseId('')} aria-label="Klasse entfernen" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ld-text-secondary)', display: 'flex', padding: 2 }}><MIcon name="close" size={15} /></button>
              </span>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MIcon name="groups" size={18} color="var(--ld-text-tertiary)" />
                <LdAuswahl value="" onChange={e => setKlasseId(e.target.value ? Number(e.target.value) : '')} aria-label="Klasse verknüpfen">
                  <option value="">Klasse verknüpfen …</option>
                  {klassenListe.map(k => <option key={k.klasse_id} value={k.klasse_id}>{k.klasse_name}</option>)}
                </LdAuswahl>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: 'var(--ld-radius-pill)', border: 'none', background: 'var(--ld-bg-input)', color: 'var(--ld-text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Abbrechen</button>
          <button onClick={handleErstellen} disabled={!titel.trim() || laden}
            style={{ flex: 2, padding: '12px', borderRadius: 'var(--ld-radius-pill)', border: 'none',
              background: !titel.trim() || laden ? 'var(--ld-bg-muted)' : 'linear-gradient(135deg,#6B5FD4,#4A3FB0)',
              color: !titel.trim() || laden ? 'var(--ld-text-secondary)' : '#fff', fontSize: 14, fontWeight: 700,
              cursor: !titel.trim() || laden ? 'not-allowed' : 'pointer',
              boxShadow: !titel.trim() || laden ? 'none' : 'var(--ld-shadow-elev-hero)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <MIcon name="check" size={18} />{laden ? 'Erstellt …' : 'Aufgabe erstellen'}
          </button>
        </div>
      </div>
    </LdSheet>
  );
}
